import {
  CONTENT_TYPES,
  inferContentType,
  payloadHasRenderableContent,
} from './inferContentType';

/** Keys that are document metadata, not narrative sections. */
const METADATA_KEYS = new Set([
  'schema_version',
  'iso',
  'country_name',
  'last_updated',
  'sections',
  'call_to_action',
  'presentation',
  'axis_catalog',
  'default_country',
  'countries',
]);

/** Top-level global keys rendered as framework cards (not in section list). */
const GLOBAL_FRAMEWORK_KEYS = new Set(['model_strengths', 'exploration_types']);

const FRAMEWORK_PRESENTATION = {
  model_strengths: {
    title: 'What every VerveStacks model includes',
    subtitle: 'Pre-instrumented before scenario design',
    icon: 'layers',
  },
  exploration_types: {
    title: 'Types of exploration',
    subtitle: 'Questions you can pose on the same substrate',
    icon: 'zap',
  },
};

/** Section manifest id → content key on the content root. */
const SECTION_PAYLOAD_ALIASES = {
  scenario_axes: 'scenario_dimensions',
};

export function humanizeKey(key) {
  if (!key || typeof key !== 'string') return '';
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatSectionTitle(title, countryName) {
  return (title || '').replace(/\{country_name\}/g, countryName || 'this country');
}

function resolvePayload(contentRoot, key) {
  if (!contentRoot) return null;
  if (contentRoot[key] !== undefined) return contentRoot[key];
  const alias = SECTION_PAYLOAD_ALIASES[key];
  if (alias && contentRoot[alias] !== undefined) return contentRoot[alias];
  return null;
}

function buildSectionEntry({ id, title, payload, required = false }) {
  const contentType = inferContentType(payload);
  const hasContent = payloadHasRenderableContent(contentType, payload);
  if (!hasContent && !required) return null;

  return {
    id,
    title,
    contentType,
    payload,
    required: Boolean(required),
    hasContent,
  };
}

function discoverContentKeys(contentRoot, excludeKeys = new Set()) {
  if (!contentRoot || typeof contentRoot !== 'object') return [];
  return Object.keys(contentRoot).filter(
    (key) => !METADATA_KEYS.has(key) && !excludeKeys.has(key) && !GLOBAL_FRAMEWORK_KEYS.has(key)
  );
}

function buildSectionsFromManifest(manifest, contentRoot, displayName) {
  const entries = [];
  const seen = new Set();

  for (const def of manifest) {
    const id = def.id;
    if (!id) continue;
    const payloadKey = SECTION_PAYLOAD_ALIASES[id] || id;
    seen.add(id);
    if (payloadKey !== id) seen.add(payloadKey);
    const payload = resolvePayload(contentRoot, id) ?? contentRoot[payloadKey];
    const title = formatSectionTitle(
      def.title || humanizeKey(id),
      displayName
    );
    const entry = buildSectionEntry({
      id,
      title,
      payload,
      required: def.required,
    });
    if (entry) entries.push(entry);
  }

  const discovered = discoverContentKeys(contentRoot, seen);
  for (const key of discovered) {
    const payload = contentRoot[key];
    const entry = buildSectionEntry({
      id: key,
      title: humanizeKey(key),
      payload,
      required: false,
    });
    if (entry) entries.push(entry);
  }

  return entries;
}

function buildSectionsAutoDiscover(contentRoot, displayName) {
  const keys = discoverContentKeys(contentRoot);
  const entries = [];

  for (const key of keys) {
    const payload = contentRoot[key];
    const entry = buildSectionEntry({
      id: key,
      title: humanizeKey(key),
      payload,
      required: false,
    });
    if (entry) entries.push(entry);
  }

  return entries;
}

function buildFrameworkBlocks(panel) {
  const blocks = [];
  for (const key of GLOBAL_FRAMEWORK_KEYS) {
    const text = panel?.[key];
    if (typeof text !== 'string' || !text.trim()) continue;
    const presentation = FRAMEWORK_PRESENTATION[key] || {
      title: humanizeKey(key),
      subtitle: null,
      icon: 'layers',
    };
    blocks.push({
      id: key,
      text,
      ...presentation,
    });
  }
  return blocks;
}

/**
 * Transform parsed YAML into a UI-ready view model.
 * @param {object} panel - Parsed YAML document
 * @param {'iso'|'global'} source
 * @param {string} displayName
 */
export function buildPanelViewModel(panel, source, displayName) {
  if (!panel) {
    return {
      frameworkBlocks: [],
      sections: [],
      countries: null,
      cta: null,
      presentation: null,
    };
  }

  const isGlobal = source === 'global';
  const contentRoot = isGlobal ? panel.default_country || {} : panel;
  const manifest = Array.isArray(panel.sections) ? panel.sections : null;

  const sections = manifest?.length
    ? buildSectionsFromManifest(manifest, contentRoot, displayName)
    : buildSectionsAutoDiscover(contentRoot, displayName);

  return {
    frameworkBlocks: isGlobal ? buildFrameworkBlocks(panel) : [],
    sections,
    countries: isGlobal ? panel.countries ?? null : null,
    cta: panel.call_to_action ?? null,
    presentation: panel.presentation ?? null,
  };
}
